import { useQuery } from "@tanstack/react-query";
import JobTitlesService from "../../services/JobTitlesService";

// API response types (snake_case from API)
interface ApiCategory {
  title_id: number;
  category_id: number;
  category_name: string;
  sub_category_id: number | null;
}

interface ApiJobTitle {
  title: string;
  is_restricted: number;
  categories: ApiCategory[];
  is_history?: boolean;
}

interface ApiJobTitlesOptions {
  options: ApiJobTitle[];
  version?: string;
}

// Client-side types (camelCase for our application)
interface Category {
  titleId: number;
  categoryId: number;
  categoryName: string;
  subCategoryId: number | null;
}

interface JobTitle {
  title: string;
  isRestricted: number;
  categories: Category[];
  isHistory?: boolean;
}

interface JobTitlesOptions {
  options: JobTitle[];
  version?: string;
}

interface JobTitlesApiResponse {
  status: boolean;
  data: {
    job_titles: ApiJobTitlesOptions;
  };
}

// Mapper functions to transform API response to client-side format
const mapCategory = (apiCategory: ApiCategory): Category => ({
  titleId: apiCategory.title_id,
  categoryId: apiCategory.category_id,
  categoryName: apiCategory.category_name,
  subCategoryId: apiCategory.sub_category_id
});

const mapJobTitle = (apiJobTitle: ApiJobTitle): JobTitle => ({
  title: apiJobTitle.title,
  isRestricted: apiJobTitle.is_restricted,
  categories: apiJobTitle.categories.map(mapCategory),
  isHistory: apiJobTitle.is_history
});

export const mapJobTitlesOptions = (
  apiData: ApiJobTitlesOptions
): JobTitlesOptions => ({
  options: apiData.options.map(mapJobTitle),
  version: apiData.version
});

// Query key factory for job titles
export const jobTitlesKeys = {
  all: ["jobTitles"] as const,
  list: () => [...jobTitlesKeys.all, "list"] as const
};

/**
 * React Query hook to fetch job titles
 * Uses React Query's built-in caching mechanism with select function for data transformation
 *
 * @param options - React Query options
 * @returns Query result with job titles data
 */
export const useJobTitlesQuery = <TData = JobTitlesOptions>(
  options: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    retry?: number;
    select?: (data: ApiJobTitlesOptions) => TData;
  } = {}
) => {
  const { select, ...queryOptions } = options;

  return useQuery({
    queryKey: jobTitlesKeys.list(),
    queryFn: async (): Promise<ApiJobTitlesOptions> => {
      try {
        const response = await JobTitlesService.getJobTitles();
        const { data } = response.data;

        // Return raw API data - transformation will happen in select function
        return data.job_titles;
      } catch (error) {
        console.error("Error fetching job titles:", error);
        throw error;
      }
    },
    // Transform API response from snake_case to camelCase using select function
    select:
      select || (mapJobTitlesOptions as (data: ApiJobTitlesOptions) => TData),
    staleTime: 1000 * 60 * 60 * 12, // 12 hours (720 minutes as per original)
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions
  });
};

/**
 * Hook to get job titles data with loading and error states
 * Provides a simplified interface similar to Redux selectors
 */
export const useJobTitles = () => {
  const { data, isLoading, error, isError } = useJobTitlesQuery();

  return {
    jobTitles: data?.options || [],
    version: data?.version || "",
    isLoading,
    error: isError ? error : null,
    hasData: !!data?.options?.length
  };
};

// Export types for use in other components
export type {
  // Client-side types (camelCase)
  JobTitle,
  Category,
  JobTitlesOptions,
  // API types (snake_case) - exported for reference but prefer using client-side types
  ApiJobTitle,
  ApiCategory,
  ApiJobTitlesOptions,
  JobTitlesApiResponse
};
